import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Apple, Plus, Trash2, Camera, FileText, PenLine, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { LogFoodModal } from "./LogFoodModal";

/**
 * FoodTrackerWidget — daily macro rings + entries list + "+ Log food" button.
 * All tiers. Coach can view read-only.
 */
export const FoodTrackerWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);

  const fetchDaily = useCallback(async () => {
    try {
      const res = await fetch(`${API}/nutrition/food/daily`, { credentials: "include" });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error("food fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDaily(); }, [fetchDaily]);

  const removeLog = async (log_id) => {
    try {
      const res = await fetch(`${API}/nutrition/food/log/${log_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) {
        toast.success("Removed");
        fetchDaily();
      }
    } catch { toast.error("Couldn't delete"); }
  };

  if (loading) return null;

  const totals = data?.totals || { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
  const targets = data?.targets || { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
  const logs = data?.logs || [];

  return (
    <>
      <div className="mb-8" data-testid="food-widget">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Apple className="w-4 h-4 text-green-400" />
            <h2 className="font-heading text-sm uppercase tracking-[0.2em] font-bold text-white">
              Today's Food
            </h2>
          </div>
          <button
            onClick={() => setShowLog(true)}
            className="flex items-center gap-1 text-xs text-text-secondary hover:text-green-400 transition-colors"
            data-testid="food-log-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Log food
          </button>
        </div>

        {/* Macro rings */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <MacroRing label="Cal" value={totals.calories} target={targets.calories} color="#10B981" />
          <MacroRing label="Protein" value={totals.protein_g} target={targets.protein_g} color="#F59E0B" unit="g" />
          <MacroRing label="Carbs" value={totals.carbs_g} target={targets.carbs_g} color="#3B82F6" unit="g" />
          <MacroRing label="Fat" value={totals.fat_g} target={targets.fat_g} color="#EC4899" unit="g" />
        </div>

        {/* Entries */}
        {logs.length === 0 ? (
          <button
            onClick={() => setShowLog(true)}
            className="w-full bg-surface border border-dashed border-border hover:border-green-500/50 rounded-xl p-5 text-center transition-colors group"
            data-testid="food-empty-cta"
          >
            <Apple className="w-7 h-7 text-text-muted group-hover:text-green-400 mx-auto mb-2 transition-colors" />
            <p className="font-heading text-sm font-bold text-white mb-0.5">Log your first meal</p>
            <p className="text-xs text-text-secondary">Photo, text, or manual — 10 seconds.</p>
          </button>
        ) : (
          <div className="bg-surface border border-border rounded-xl divide-y divide-border">
            {logs.map((row) => (
              <FoodRow key={row.log_id} row={row} onDelete={() => removeLog(row.log_id)} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showLog && (
          <LogFoodModal
            onClose={() => setShowLog(false)}
            onLogged={() => {
              setShowLog(false);
              fetchDaily();
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ================== MACRO RING ==================
const MacroRing = ({ label, value, target, color, unit = "" }) => {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  const r = 28;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="bg-surface border border-border rounded-xl p-3 text-center" data-testid={`macro-ring-${label.toLowerCase()}`}>
      <div className="relative w-16 h-16 mx-auto mb-1">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} fill="none" stroke="#1E293B" strokeWidth="5" />
          {target > 0 && (
            <circle
              cx="32" cy="32" r={r}
              fill="none" stroke={color} strokeWidth="5"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 0.5s ease" }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-white">{Math.round(value)}</span>
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-text-muted font-bold">{label}</p>
      <p className="text-[10px] text-text-secondary">
        {target > 0 ? `/${Math.round(target)}${unit}` : `${unit || ""}`}
      </p>
    </div>
  );
};

// ================== FOOD ROW ==================
const FoodRow = ({ row, onDelete }) => {
  const sourceIcon = {
    label_photo: <FileText className="w-3 h-3" />,
    meal_photo: <Camera className="w-3 h-3" />,
    quick_add: <Sparkles className="w-3 h-3" />,
    manual: <PenLine className="w-3 h-3" />,
  }[row.source] || <PenLine className="w-3 h-3" />;

  return (
    <div className="p-3 flex items-start gap-3" data-testid={`food-row-${row.log_id}`}>
      <div className="w-8 h-8 bg-[#0B0F1A] rounded-lg flex items-center justify-center flex-shrink-0 text-green-400">
        {sourceIcon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-bold text-white truncate">{row.name}</p>
          {row.is_estimate && (
            <span className="text-[9px] uppercase tracking-widest bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">
              Est
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-text-secondary mt-0.5">
          <span className="text-white font-semibold">{Math.round(row.calories)} cal</span>
          <span>P {Math.round(row.protein_g)}</span>
          <span>C {Math.round(row.carbs_g)}</span>
          <span>F {Math.round(row.fat_g)}</span>
        </div>
      </div>
      <button
        onClick={onDelete}
        className="text-text-muted hover:text-rose-400 p-1 transition-colors"
        data-testid={`food-row-delete-${row.log_id}`}
        aria-label="Delete entry"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default FoodTrackerWidget;
